/*
 * canasta-builder.js — «Arma tu canasta» en 3 pasos (maqueta #380).
 *
 * IIFE vanilla al estilo de assets/winerd-configurator.js — sin dependencias,
 * sin bundle, no toca theme.js. El markup lo pone
 * sections/canasta-builder.liquid; el CSS vive en assets/winerd.css +
 * assets/canasta.css, todo bajo .winerd-scope.
 *
 * Paso 1: las tarjetas de canasta vienen renderizadas en Liquid con su cupo
 * en data-*. Paso 2: catálogo por pestañas cargado en vivo (products.json) +
 * canasta lateral / hoja inferior móvil. Al buscar (2+ letras) también se
 * pregunta a la búsqueda de Shopify (/search?view=canasta →
 * templates/search.canasta.liquid) para encontrar productos de la colección
 * que no entraron en la carga inicial. Paso 3: dedicatoria con tarjeta en
 * vivo, resumen agrupado y un único POST /cart/add.js (canasta + piezas, todas
 * con la propiedad visible «Canasta: <tipo> · CB-xxxx»); nunca vacía el
 * carrito y refresca el drawer del tema con cart:refresh. Los ajustes de la
 * sección (reglas de cupo, pestañas…) llegan en el JSON [data-cb-config].
 *
 * Reglas de cupo (ajuste regla_cupo), resueltas en una sola función cupo():
 *   · fijo   → botellas ≤ B y gourmet ≤ G, por separado.
 *   · mezcla → botellas + gourmet ≤ B + G, con min_botellas ≤ botellas ≤
 *              max_botellas (tope físico, nunca mayor que B + G).
 */
(function () {
  "use strict";

  var STEP_COUNT = 3;
  var LIMIT = 100;
  /* Tope de páginas de la carga inicial por pestaña (5 × 250 = 1250
     productos): evita colgar el navegador con una colección gigante. Lo que
     quede fuera lo encuentra el buscador (searchTab), no este tope. */
  var MAX_PAGES = 2;
  /* Búsqueda en toda la tienda: desde 2 letras, hasta 3 páginas de 50. */
  var SEARCH_MIN = 2;
  var SEARCH_PAGES = 3;
  /* Tarjetas por tanda: el resto se pinta con «Ver más». */
  var PAGE_SIZE = 24;
  var PRICES = [
    { id: "all", label: "Cualquier precio", min: 0, max: Infinity },
    { id: "a", label: "Hasta $500", min: 0, max: 500 },
    { id: "b", label: "$500 – $1,000", min: 500.01, max: 1000 },
    { id: "c", label: "$1,000 – $2,000", min: 1000.01, max: 2000 },
    { id: "d", label: "Más de $2,000", min: 2000.01, max: Infinity }
  ];
  var CONFETTI = ["#720026", "#003049", "#c9a24a", "#2e8b57", "#d62828"];
  var NAME_MAX = 40;
  var MSG_MAX = 200;

  function newGift() {
    return { on: true, from: "", to: "", msg: "", hide: true };
  }

  /* Código corto que une la canasta con sus piezas en el carrito y en el almacén. */
  function basketCode() {
    return "CB-" + (Math.random().toString(36).slice(2) + "0000").slice(0, 4).toUpperCase();
  }

  function toArray(nodeList) {
    return Array.prototype.slice.call(nodeList);
  }

  function clampStep(n) {
    if (isNaN(n)) return 1;
    return Math.min(STEP_COUNT, Math.max(1, n));
  }

  function money(value) {
    var n = Number(value) || 0;
    try {
      return new Intl.NumberFormat("es-MX", {
        style: "currency",
        currency: "MXN",
        maximumFractionDigits: 0
      }).format(n);
    } catch (e) {
      return "$" + Math.round(n);
    }
  }

  function shopRoot() {
    try {
      if (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) {
        return window.Shopify.routes.root;
      }
    } catch (e) {}
    return "/";
  }

  function esc(value) {
    return String(value == null ? "" : value).replace(/[&<>"']/g, function (c) {
      return { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c];
    });
  }

  function plural(n, one, many) {
    return n + " " + (n === 1 ? one : many);
  }

  function norm(value) {
    return String(value || "")
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase();
  }

  function reduceMotion() {
    return !!(window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches);
  }

  /* Hoja inferior en vez de panel lateral: mismo corte que canasta.css. */
  function isPocket() {
    return !!(window.matchMedia && window.matchMedia("(max-width: 1000px)").matches);
  }

  /* Imagen del CDN de Shopify al ancho pedido (acepta ?width=). Las de la
     búsqueda ya vienen con width (image_url lo exige): se reemplaza. */
  function sizedImage(src, width) {
    if (!src) return "";
    src = src.replace(/([?&])width=\d+&?/, "$1").replace(/[?&]$/, "");
    return src + (src.indexOf("?") === -1 ? "?" : "&") + "width=" + width;
  }

  /* products.json → lo mínimo que usa el builder, con la primera variante
     disponible (es la que se agrega al carrito en el paso 3). */
  function normalizeProduct(product, tab) {
    var variants = Array.isArray(product.variants) ? product.variants : [];
    var variant = variants.filter(function (v) { return v.available !== false; })[0];
    if (!variant) return null;
    var image = Array.isArray(product.images) && product.images.length ? product.images[0] : null;
    var src = image ? (typeof image === "string" ? image : image.src || "") : "";
    var tags = Array.isArray(product.tags) ? product.tags.join(" ") : product.tags || "";
    return {
      key: String(variant.id),
      productId: product.id,
      handle: product.handle,
      title: product.title || "",
      vendor: product.vendor || "",
      kind: product.product_type || "",
      price: parseFloat(variant.price) || 0,
      image: src,
      type: tab.type,
      tabId: tab.id,
      haystack: norm([product.title, product.vendor, product.product_type, tags].join(" "))
    };
  }

  /* search.canasta → forma de products.json, para pasar por normalizeProduct
     (la variante ya viene filtrada como disponible; el precio, en centavos). */
  function fromSearch(p) {
    return {
      id: p.id,
      handle: p.handle,
      title: p.title,
      vendor: p.vendor,
      product_type: p.product_type,
      tags: p.tags,
      images: p.image ? [p.image] : [],
      variants: p.variant ? [{ id: p.variant, price: (Number(p.price) || 0) / 100, available: true }] : []
    };
  }

  function readConfig(root) {
    var el = root.querySelector("[data-cb-config]");
    var cfg = {};
    try {
      cfg = JSON.parse(el ? el.textContent : "{}") || {};
    } catch (e) {
      if (window.console) console.warn("Arma tu canasta: config inválida", e);
    }
    var minBottles = parseInt(cfg.minBottles, 10);
    var maxBottles = parseInt(cfg.maxBottles, 10);
    var freeShip = parseInt(cfg.freeShip, 10);
    return {
      rule: cfg.rule === "mezcla" ? "mezcla" : "fijo",
      minBottles: isNaN(minBottles) || minBottles < 0 ? 0 : minBottles,
      maxBottles: isNaN(maxBottles) || maxBottles < 1 ? Infinity : maxBottles,
      requireFull: cfg.requireFull !== false,
      freeShip: isNaN(freeShip) || freeShip < 0 ? 0 : freeShip,
      showSuggestions: cfg.showSuggestions !== false,
      giftQuick: String(cfg.giftQuick || "")
        .split(/\r?\n/)
        .map(function (s) { return s.trim(); })
        .filter(Boolean),
      tabs: (Array.isArray(cfg.tabs) ? cfg.tabs : [])
        .filter(function (t) { return t && t.collection; })
        .map(function (t) {
          return {
            id: String(t.id),
            label: t.label || t.collection,
            collection: t.collection,
            total: Number(t.total) || 0,
            type: t.type === "gourmet" ? "gourmet" : "botella"
          };
        })
    };
  }

  class CanastaBuilder {
    constructor(root) {
      this.root = root;
      this.config = readConfig(root);
      this.panels = toArray(root.querySelectorAll("[data-cb-panel]"));
      this.steps = toArray(root.querySelectorAll("[data-cb-step]"));
      this.lines = toArray(root.querySelectorAll(".multistep-progress .ms-line"));
      this.builder = root.querySelector("[data-cb-builder]");
      this.sheet = root.querySelector("[data-cb-sheet]");
      this.catalogEl = root.querySelector("[data-cb-catalog]");
      this.basketViews = toArray(root.querySelectorAll("[data-cb-basket-view]"));
      this.flyLayer = root.querySelector("[data-cb-fly]");
      this.giftEl = root.querySelector("[data-cb-gift]");

      this.baskets = toArray(root.querySelectorAll("[data-cb-basket]")).map(function (card) {
        return {
          id: card.dataset.cbBasket,
          card: card,
          warn: card.querySelector("[data-cb-warn]"),
          variantId: card.dataset.variantId || "",
          title: card.dataset.title || "",
          price: (parseInt(card.dataset.price, 10) || 0) / 100,
          bottles: parseInt(card.dataset.bottles, 10) || 0,
          gourmet: parseInt(card.dataset.gourmet, 10) || 0,
          available: !card.hasAttribute("data-cb-unavailable")
        };
      });

      this.state = {
        step: 1,
        basketId: null,
        /* piezas elegidas en el paso 2, en orden de llegada (una por unidad):
           { uid, key (variant id), type: "botella"|"gourmet", tabId, title, … } */
        items: [],
        tabId: this.config.tabs.length ? this.config.tabs[0].id : null,
        /* búsqueda de la pestaña activa; cada pestaña guarda la suya en
           `queries` para que buscar en vinos no vacíe destilados o gourmet */
        query: "",
        queries: {},
        price: "all",
        sort: "top",
        shown: PAGE_SIZE,
        gift: newGift(),
        /* los errores de De/Para se muestran después del primer intento de agregar */
        tried: false,
        adding: false
      };
      /* caché por pestaña: { status: "loading"|"ready"|"error", products: [] } */
      this.catalogs = {};
      /* caché de búsquedas por pestaña + término, misma forma que catalogs */
      this.searches = {};
      this.uid = 1;

      this.booting = true;
      this.init();
      this.booting = false;
    }

    init() {
      this.bindNav();
      this.bindBaskets();
      this.bindCatalog();
      this.bindBasketViews();
      this.bindGift();
      this.renderGiftShell();

      /* Arranca con la primera canasta disponible: el paso 2 siempre tiene cupo. */
      var first = this.baskets.filter(function (b) { return b.available; })[0];
      if (first) this.selectBasket(first.id);
      this.renderCatalogShell();
      this.update();
      this.goPanel(1);
    }

    /* ---------- cupo ---------- */

    basket() {
      var id = this.state.basketId;
      return this.baskets.filter(function (b) { return b.id === id; })[0] || null;
    }

    counts() {
      var c = { botella: 0, gourmet: 0, total: this.state.items.length };
      this.state.items.forEach(function (item) {
        if (item.type === "gourmet") c.gourmet++;
        else c.botella++;
      });
      return c;
    }

    /*
     * Única fuente de verdad del cupo: botones deshabilitados, contador
     * «te caben…», bloqueo del paso 1 y canContinue salen de aquí.
     *   room.botella / room.gourmet → cuántas más de cada tipo caben
     *   overflow → cuántas piezas sobran para caber (al bajar de canasta)
     *   full     → no cabe nada más; meetsMin → cumple min_botellas
     */
    cupo(basket) {
      var b = basket || this.basket();
      var c = this.counts();
      if (!b) {
        return { counts: c, capacity: 0, room: { botella: 0, gourmet: 0 }, free: 0, overflow: 0, full: false, meetsMin: false };
      }
      var room, capacity, overflow, meetsMin, minB = 0, maxB = b.bottles;
      if (this.config.rule === "mezcla") {
        capacity = b.bottles + b.gourmet;
        maxB = Math.min(this.config.maxBottles, capacity);
        minB = Math.min(this.config.minBottles, maxB);
        var free = Math.max(0, capacity - c.total);
        room = {
          botella: Math.min(free, Math.max(0, maxB - c.botella)),
          /* el gourmet no puede comerse los lugares reservados al mínimo de botellas */
          gourmet: Math.min(free, Math.max(0, capacity - Math.max(minB, c.botella) - c.gourmet))
        };
        overflow = Math.max(0, c.total - capacity, c.botella - maxB);
        meetsMin = c.botella >= minB;
      } else {
        capacity = b.bottles + b.gourmet;
        room = {
          botella: Math.max(0, b.bottles - c.botella),
          gourmet: Math.max(0, b.gourmet - c.gourmet)
        };
        overflow = Math.max(0, c.botella - b.bottles) + Math.max(0, c.gourmet - b.gourmet);
        meetsMin = true;
      }
      return {
        counts: c,
        capacity: capacity,
        minBottles: minB,
        maxBottles: maxB,
        room: room,
        free: room.botella + room.gourmet,
        overflow: overflow,
        full: overflow === 0 && room.botella === 0 && room.gourmet === 0,
        meetsMin: meetsMin
      };
    }

    overflow(basket) {
      return this.cupo(basket).overflow;
    }

    isComplete() {
      return this.cupo().full;
    }

    canContinue() {
      var q = this.cupo();
      if (!this.basket() || q.counts.total === 0 || q.overflow > 0 || !q.meetsMin) return false;
      return this.config.requireFull ? q.full : true;
    }

    /* «Te caben…»: lo que falta, dicho por tipo. */
    roomText(q) {
      if (q.full) return "Tu canasta está completa.";
      var parts = [];
      if (this.config.rule === "mezcla") {
        var free = q.capacity - q.counts.total;
        var text = "Te " + (free === 1 ? "cabe " : "caben ") + plural(free, "producto", "productos") + " más";
        if (q.room.botella < free) {
          text += q.room.botella
            ? " (hasta " + plural(q.room.botella, "botella", "botellas") + ")"
            : " (solo gourmet: ya llegaste al tope de botellas)";
        }
        parts.push(text + ".");
        var missing = q.minBottles - q.counts.botella;
        if (missing > 0) parts.push("Lleva al menos " + plural(missing, "botella más", "botellas más") + ".");
        return parts.join(" ");
      }
      if (q.room.botella) parts.push(plural(q.room.botella, "botella", "botellas"));
      if (q.room.gourmet) parts.push(q.room.gourmet + " gourmet");
      var n = q.room.botella + q.room.gourmet;
      return "Te " + (n === 1 ? "cabe " : "caben ") + parts.join(" y ") + " más.";
    }

    ctaLabel() {
      if (this.canContinue()) return "Continuar a la dedicatoria";
      var q = this.cupo();
      if (!q.counts.total) return "Agrega productos para continuar";
      if (q.overflow) return "Quita " + plural(q.overflow, "producto", "productos");
      if (!q.meetsMin) {
        var missing = q.minBottles - q.counts.botella;
        return "Te " + (missing === 1 ? "falta 1 botella" : "faltan " + missing + " botellas");
      }
      var left = q.free;
      return "Te " + (left === 1 ? "falta 1 producto" : "faltan " + left + " productos");
    }

    /* ---------- paso 1 · canasta ---------- */

    bindBaskets() {
      var self = this;
      this.baskets.forEach(function (b) {
        b.card.addEventListener("click", function () {
          self.selectBasket(b.id);
        });
      });
    }

    selectBasket(id) {
      var b = this.baskets.filter(function (x) { return x.id === id; })[0];
      if (!b || !b.available) return;
      /* No dejar bajar a una canasta donde ya no cabe lo elegido. */
      var extra = this.overflow(b);
      if (extra > 0) {
        this.toast("Quita " + plural(extra, "producto", "productos") + " para elegir esta canasta");
        return;
      }
      this.state.basketId = id;
      if (!this.booting) this.update();
    }

    renderBaskets() {
      var self = this;
      this.baskets.forEach(function (b) {
        var active = b.id === self.state.basketId;
        var extra = b.available ? self.overflow(b) : 0;
        b.card.classList.toggle("is-active", active);
        b.card.setAttribute("aria-pressed", active ? "true" : "false");
        b.card.disabled = !b.available || extra > 0;
        if (b.warn) {
          b.warn.hidden = extra === 0;
          b.warn.textContent = extra
            ? "Quita " + plural(extra, "producto", "productos") + " para elegir esta canasta."
            : "";
        }
      });
    }

    /* ---------- paso 2 · catálogo ---------- */

    tab() {
      var id = this.state.tabId;
      return this.config.tabs.filter(function (t) { return t.id === id; })[0] || null;
    }

    renderCatalogShell() {
      if (!this.catalogEl) return;
      if (!this.config.tabs.length) {
        this.catalogEl.innerHTML =
          '<div class="catalog-empty"><strong>Sin pestañas de catálogo</strong>' +
          "<p>Agrega bloques «Pestaña de catálogo» en el editor de temas.</p></div>";
        return;
      }
      var tabs = this.config.tabs
        .map(function (t, i) {
          return (
            '<button type="button" role="tab" class="catalog-tab" data-cb-tab="' + esc(t.id) + '">' +
            '<span class="cb-tabnum">' + (i + 1) + "</span>" + esc(t.label) +
            '<span data-cb-tabdot="' + esc(t.id) + '"></span></button>'
          );
        })
        .join("");
      var prices = PRICES.map(function (p) {
        return (
          '<button type="button" class="catalog-tab catalog-tab--sm" data-cb-price="' + p.id + '">' +
          esc(p.label) + "</button>"
        );
      }).join("");

      this.catalogEl.innerHTML =
        '<div class="wine-browser__head">' +
          '<div><span class="mini-label">Catálogo de Alcornoque</span><h4>Elige lo que va en tu canasta</h4></div>' +
          '<div class="catalog-meta"><span class="catalog-status" data-cb-status>Cargando catálogo…</span><span data-cb-total></span></div>' +
        "</div>" +
        '<div class="catalog-search">' +
          '<input type="search" class="catalog-search__input" data-cb-search placeholder="Busca por nombre, marca o tipo…" aria-label="Buscar productos para tu canasta" autocomplete="off">' +
          '<button type="button" class="catalog-search__clear" data-cb-search-clear hidden>Limpiar</button>' +
        "</div>" +
        '<div class="catalog-category-tabs" role="tablist" aria-label="Categorías">' + tabs + "</div>" +
        '<div class="catalog-category-tabs" aria-label="Filtrar por precio"><span class="cb-filter-label">Precio</span>' + prices + "</div>" +
        '<div class="catalog-toolbar">' +
          '<p class="cb-room" data-cb-room aria-live="polite"></p>' +
          '<label class="catalog-sort"><span>Ordenar</span><select data-cb-sort>' +
            '<option value="top">Destacados</option>' +
            '<option value="asc">Precio: menor a mayor</option>' +
            '<option value="desc">Precio: mayor a menor</option>' +
            '<option value="az">Nombre A-Z</option>' +
          "</select></label>" +
        "</div>" +
        '<div class="wine-grid" data-cb-grid aria-live="polite"></div>' +
        '<div class="cb-more" data-cb-more hidden><button type="button" class="btn btn--outline btn--sm" data-cb-more-btn>Ver más</button></div>';

      this.gridEl = this.catalogEl.querySelector("[data-cb-grid]");
    }

    bindCatalog() {
      var self = this;
      if (!this.catalogEl) return;
      var searchTimer;
      this.catalogEl.addEventListener("input", function (event) {
        if (!event.target.matches("[data-cb-search]")) return;
        clearTimeout(searchTimer);
        searchTimer = setTimeout(function () {
          self.state.query = event.target.value;
          self.state.shown = PAGE_SIZE;
          self.renderCatalog();
        }, 150);
      });
      this.catalogEl.addEventListener("change", function (event) {
        if (!event.target.matches("[data-cb-sort]")) return;
        self.state.sort = event.target.value;
        self.state.shown = PAGE_SIZE;
        self.renderCatalog();
      });
      this.catalogEl.addEventListener("click", function (event) {
        var t = event.target;
        var tab = t.closest("[data-cb-tab]");
        if (tab) {
          clearTimeout(searchTimer);
          var input = self.catalogEl.querySelector("[data-cb-search]");
          if (self.state.tabId) self.state.queries[self.state.tabId] = input ? input.value : self.state.query;
          self.state.tabId = tab.dataset.cbTab;
          self.state.query = self.state.queries[self.state.tabId] || "";
          if (input) input.value = self.state.query;
          // El rango de precio no se arrastra a la otra pestaña: un rango de
          // vinos deja Destilados o Gourmet vacíos sin que se note por qué.
          self.state.price = "all";
          self.state.shown = PAGE_SIZE;
          self.renderCatalog();
          return;
        }
        var price = t.closest("[data-cb-price]");
        if (price) {
          self.state.price = price.dataset.cbPrice;
          self.state.shown = PAGE_SIZE;
          self.renderCatalog();
          return;
        }
        if (t.closest("[data-cb-search-clear]") || t.closest("[data-cb-clear-filters]")) {
          var input = self.catalogEl.querySelector("[data-cb-search]");
          if (input) input.value = "";
          self.state.query = "";
          if (t.closest("[data-cb-clear-filters]")) self.state.price = "all";
          self.state.shown = PAGE_SIZE;
          self.renderCatalog();
          return;
        }
        if (t.closest("[data-cb-more-btn]")) {
          self.state.shown += PAGE_SIZE;
          self.renderCatalog(true);
          return;
        }
        if (t.closest("[data-cb-retry]")) {
          delete self.catalogs[self.state.tabId];
          self.renderCatalog();
          return;
        }
        var add = t.closest("[data-cb-add]");
        if (add && !add.disabled) {
          var card = add.closest(".cb-card");
          self.addItem(self.findProduct(add.dataset.cbAdd), card && card.querySelector(".cb-card__media"));
          return;
        }
        var dec = t.closest("[data-cb-dec]");
        if (dec) self.removeOne(dec.dataset.cbDec);
      });
    }

    /* Carga perezosa: una pestaña se pide la primera vez que se abre y queda
       en caché. Paginado igual que assets/winerd-configurator.js. */
    loadTab(tab) {
      var self = this;
      if (this.catalogs[tab.id]) return;
      var entry = (this.catalogs[tab.id] = { status: "loading", products: [] });
      var base = shopRoot();
      if (base.charAt(base.length - 1) !== "/") base += "/";
      var all = [];

      function fetchPage(page) {
        var url =
          base + "collections/" + encodeURIComponent(tab.collection) +
          "/products.json?limit=" + LIMIT + "&page=" + page;
        return fetch(url, { headers: { Accept: "application/json" }, credentials: "same-origin" })
          .then(function (res) {
            if (!res.ok) throw new Error("HTTP " + res.status);
            return res.json();
          })
          .then(function (data) {
            var products = data && Array.isArray(data.products) ? data.products : [];
            all = all.concat(products);
            if (products.length === LIMIT && page < MAX_PAGES) return fetchPage(page + 1);
            return all;
          });
      }

      fetchPage(1)
        .then(function (products) {
          entry.products = products
            .map(function (p) { return normalizeProduct(p, tab); })
            .filter(Boolean);
          entry.status = "ready";
        })
        .catch(function (error) {
          if (window.console) console.warn("Arma tu canasta: no se pudo cargar " + tab.collection, error);
          entry.status = "error";
        })
        .then(function () {
          if (self.catalogs[tab.id] !== entry) return;
          if (self.state.tabId === tab.id) self.renderCatalog();
          self.renderBasket();
        });
    }

    /* Búsqueda en toda la tienda: la carga inicial topa en MAX_PAGES, así
       que lo que no entró se pide a /search (templates/search.canasta.liquid)
       y se queda solo lo de la colección de la pestaña, con su tipo. Caché
       por pestaña + término; null si el término es corto. */
    searchTab(tab, query) {
      var self = this;
      var term = query.trim();
      if (term.length < SEARCH_MIN) return null;
      var id = tab.id + "\n" + norm(term);
      if (this.searches[id]) return this.searches[id];
      /* solo vale la última tecla: corta la búsqueda anterior aún en curso */
      if (this.searchAbort) this.searchAbort.abort();
      var ctrl = window.AbortController ? new AbortController() : null;
      this.searchAbort = ctrl;
      var entry = (this.searches[id] = { status: "loading", products: [] });
      var base = shopRoot();
      if (base.charAt(base.length - 1) !== "/") base += "/";
      var all = [];

      function fetchPage(page) {
        var url =
          base + "search?q=" + encodeURIComponent(term) +
          "&type=product&view=canasta&options%5Bunavailable_products%5D=hide&options%5Bprefix%5D=last&page=" + page;
        return fetch(url, {
          headers: { Accept: "application/json" },
          credentials: "same-origin",
          signal: ctrl ? ctrl.signal : undefined
        })
          .then(function (res) {
            if (!res.ok) throw new Error("HTTP " + res.status);
            return res.json();
          })
          .then(function (data) {
            var products = data && Array.isArray(data.products) ? data.products : [];
            all = all.concat(products);
            var pages = parseInt(data && data.pages, 10) || 1;
            if (page < pages && page < SEARCH_PAGES) return fetchPage(page + 1);
            return all;
          });
      }

      fetchPage(1)
        .then(function (products) {
          entry.products = products
            .filter(function (p) { return Array.isArray(p.collections) && p.collections.indexOf(tab.collection) !== -1; })
            .map(function (p) { return normalizeProduct(fromSearch(p), tab); })
            .filter(Boolean);
          entry.status = "ready";
        })
        .catch(function (error) {
          /* cortada por una tecla nueva: se olvida para poder repetirla */
          if (error && error.name === "AbortError") {
            if (self.searches[id] === entry) delete self.searches[id];
            return;
          }
          /* sin búsqueda remota se queda el filtrado local (renderCatalog) */
          if (window.console) console.warn("Arma tu canasta: no se pudo buscar «" + term + "»", error);
          entry.status = "error";
        })
        .then(function () {
          if (self.searches[id] !== entry) return;
          if (self.searchAbort === ctrl) self.searchAbort = null;
          if (self.state.tabId === tab.id && norm(self.state.query.trim()) === norm(term)) self.renderCatalog();
        });
      return entry;
    }

    /* La pestaña activa manda: si un producto está en dos pestañas, cuenta
       con el tipo de la que se está viendo. Incluye lo hallado por searchTab. */
    findProduct(key) {
      var self = this;
      var tabId = this.state.tabId;
      var searches = Object.keys(this.searches);
      var entries = [this.catalogs[tabId]]
        .concat(searches.filter(function (id) { return id.split("\n")[0] === tabId; }).map(function (id) { return self.searches[id]; }))
        .concat(Object.keys(this.catalogs).map(function (id) { return self.catalogs[id]; }))
        .concat(searches.map(function (id) { return self.searches[id]; }));
      for (var i = 0; i < entries.length; i++) {
        var entry = entries[i];
        if (!entry) continue;
        for (var j = 0; j < entry.products.length; j++) {
          if (entry.products[j].key === key) return entry.products[j];
        }
      }
      return null;
    }

    /* Filtrado local de la pestaña + lo que encontró la búsqueda de Shopify
       (`found`, ya coincide con el término —también por descripción o
       erratas—, así que solo pasa por el filtro de precio), sin duplicados. */
    filteredList(entry, found) {
      var price = PRICES.filter(function (p) { return p.id === this.state.price; }, this)[0] || PRICES[0];
      var terms = norm(this.state.query.trim()).split(/\s+/).filter(Boolean);
      var inPrice = function (p) { return p.price >= price.min && p.price <= price.max; };
      var seen = {};
      var list = entry.products.filter(function (p) {
        if (!inPrice(p)) return false;
        if (!terms.every(function (term) { return p.haystack.indexOf(term) !== -1; })) return false;
        seen[p.key] = true;
        return true;
      });
      if (found && found.status === "ready") {
        found.products.forEach(function (p) {
          if (seen[p.key] || !inPrice(p)) return;
          seen[p.key] = true;
          list.push(p);
        });
      }
      var sort = this.state.sort;
      if (sort === "asc") list.sort(function (a, b) { return a.price - b.price; });
      else if (sort === "desc") list.sort(function (a, b) { return b.price - a.price; });
      else if (sort === "az") list.sort(function (a, b) { return a.title.localeCompare(b.title, "es"); });
      return list;
    }

    /* Pinta la rejilla de la pestaña activa. `append` = «Ver más» (no
       repite la animación de entrada de las tarjetas ya visibles). */
    renderCatalog(append) {
      if (!this.gridEl) return;
      var self = this;
      var tab = this.tab();
      var root = this.catalogEl;

      toArray(root.querySelectorAll("[data-cb-tab]")).forEach(function (btn) {
        var on = btn.dataset.cbTab === self.state.tabId;
        btn.classList.toggle("is-active", on);
        btn.setAttribute("aria-selected", on ? "true" : "false");
      });
      toArray(root.querySelectorAll("[data-cb-price]")).forEach(function (btn) {
        var on = btn.dataset.cbPrice === self.state.price;
        btn.classList.toggle("is-active", on);
        btn.setAttribute("aria-pressed", on ? "true" : "false");
      });
      var clear = root.querySelector("[data-cb-search-clear]");
      if (clear) clear.hidden = !this.state.query;
      var input = root.querySelector("[data-cb-search]");
      if (input && tab) input.placeholder = "Busca en " + tab.label.toLowerCase() + "…";

      if (!tab) return;
      this.loadTab(tab);
      var entry = this.catalogs[tab.id];
      var status = root.querySelector("[data-cb-status]");
      var total = root.querySelector("[data-cb-total]");
      var more = root.querySelector("[data-cb-more]");
      more.hidden = true;

      if (entry.status === "loading") {
        this.setStatus(status, "", "Cargando catálogo…");
        total.textContent = "";
        this.gridEl.innerHTML =
          '<div class="catalog-loading"><span></span><span></span><span></span>' +
          "<p>Buscando " + esc(tab.label.toLowerCase()) + " en Alcornoque</p></div>";
        return;
      }
      if (entry.status === "error") {
        this.setStatus(status, "is-fallback", "No pudimos cargar el catálogo");
        total.textContent = "";
        this.gridEl.innerHTML =
          '<div class="catalog-empty"><strong>No pudimos cargar ' + esc(tab.label.toLowerCase()) + "</strong>" +
          '<p>Revisa tu conexión e inténtalo de nuevo.</p>' +
          '<button type="button" class="btn btn--outline btn--sm" data-cb-retry>Reintentar</button></div>';
        return;
      }

      var found = this.searchTab(tab, this.state.query);
      var searching = !!found && found.status === "loading";
      var list = this.filteredList(entry, found);
      if (searching) this.setStatus(status, "", "Buscando en toda la tienda…");
      else this.setStatus(status, "is-live", "Catálogo en vivo");
      /* Sin búsqueda ni filtro de precio, el contador muestra el total real de
         la colección (la carga inicial topa en MAX_PAGES); con ellos, los resultados. */
      var count = !this.state.query.trim() && this.state.price === "all" ? Math.max(tab.total, list.length) : list.length;
      total.textContent = searching && !list.length ? "" : count.toLocaleString("es-MX") + (count === 1 ? " producto" : " productos");

      if (!list.length && searching) {
        this.gridEl.innerHTML =
          '<div class="catalog-loading"><span></span><span></span><span></span>' +
          "<p>Buscando " + esc(tab.label.toLowerCase()) + " en toda la tienda</p></div>";
        return;
      }
      if (!list.length) {
        this.gridEl.innerHTML =
          '<div class="catalog-empty"><strong>No encontramos productos con esos filtros</strong>' +
          "<p>Prueba con otra palabra o quita el filtro de precio.</p>" +
          '<button type="button" class="btn btn--outline btn--sm" data-cb-clear-filters>Limpiar filtros</button></div>';
        return;
      }

      var from = append ? this.gridEl.querySelectorAll(".cb-card").length : 0;
      var shown = list.slice(from, this.state.shown);
      var html = shown.map(function (p, i) { return self.cardHtml(p, i); }).join("");
      if (append) this.gridEl.insertAdjacentHTML("beforeend", html);
      else this.gridEl.innerHTML = html;
      more.hidden = list.length <= this.state.shown;
      this.updateCards();
    }

    setStatus(el, kind, text) {
      if (!el) return;
      el.classList.toggle("is-live", kind === "is-live");
      el.classList.toggle("is-fallback", kind === "is-fallback");
      el.textContent = text;
    }

    cardHtml(p, i) {
      var media = p.image
        ? '<img src="' + esc(sizedImage(p.image, 360)) + '" srcset="' +
          esc(sizedImage(p.image, 240)) + " 240w, " + esc(sizedImage(p.image, 360)) + " 360w, " +
          esc(sizedImage(p.image, 540)) + ' 540w" sizes="(max-width: 620px) 50vw, 220px" alt="' +
          esc(p.title) + '" loading="lazy" width="360" height="360">'
        : '<span class="wine-card__image-fallback">Sin foto</span>';
      return (
        '<article class="wine-card cb-card" data-cb-card="' + esc(p.key) + '" style="--i:' + Math.min(i, 11) + '">' +
          '<div class="cb-card__media">' + media + '<span data-cb-qty></span></div>' +
          '<div class="wine-card__copy">' +
            (p.kind ? "<span>" + esc(p.kind) + "</span>" : "") +
            "<h4>" + esc(p.title) + "</h4>" +
            (p.vendor ? '<div class="cb-card__vendor">' + esc(p.vendor) + "</div>" : "") +
            "<p>" + money(p.price) + "</p>" +
            '<div class="cb-card__ctl" data-cb-ctl></div>' +
          "</div>" +
        "</article>"
      );
    }

    qty() {
      var m = {};
      this.state.items.forEach(function (it) { m[it.key] = (m[it.key] || 0) + 1; });
      return m;
    }

    /* Refresca solo el stepper/badge de cada tarjeta (no re-anima la rejilla). */
    updateCards() {
      if (!this.gridEl) return;
      var tab = this.tab();
      if (!tab) return;
      var qty = this.qty();
      var q = this.cupo();
      var room = q.room[tab.type];
      var full = room === 0;
      var noun = tab.type === "gourmet" ? "Ya no cabe gourmet" : "Ya no caben botellas";
      var label = q.full ? "Canasta llena" : noun;

      toArray(this.gridEl.querySelectorAll("[data-cb-card]")).forEach(function (card) {
        var key = card.dataset.cbCard;
        var n = qty[key] || 0;
        var title = (card.querySelector("h4") || {}).textContent || "";
        card.classList.toggle("is-in", n > 0);
        var stamp = n + ":" + (full ? 1 : 0) + ":" + label;
        if (card.dataset.cbStamp === stamp) return;
        var prevN = card.dataset.cbStamp ? parseInt(card.dataset.cbStamp, 10) : -1;
        card.dataset.cbStamp = stamp;

        var badge = card.querySelector("[data-cb-qty]");
        if (prevN !== n) {
          badge.className = n ? "cb-card__qty" : "";
          badge.setAttribute("aria-hidden", "true");
          badge.textContent = n ? "×" + n : "";
        }
        var ctl = card.querySelector("[data-cb-ctl]");
        if (!n) {
          ctl.innerHTML =
            '<button type="button" class="catalog-add" data-cb-add="' + esc(key) + '"' +
            (full ? " disabled" : "") + ">" + (full ? label : "Agregar") + "</button>";
        } else if (prevN > 0 && ctl.querySelector(".cb-stepper")) {
          ctl.querySelector("b").outerHTML = '<b aria-live="polite">' + n + " en tu canasta</b>";
          ctl.querySelector("[data-cb-add]").disabled = full;
        } else {
          ctl.innerHTML =
            '<div class="cb-stepper" role="group" aria-label="Cantidad de ' + esc(title) + '">' +
              '<button type="button" aria-label="Quitar uno" data-cb-dec="' + esc(key) + '">−</button>' +
              '<b aria-live="polite">' + n + " en tu canasta</b>" +
              '<button type="button" aria-label="Agregar uno más" data-cb-add="' + esc(key) + '"' +
              (full ? " disabled" : "") + ">+</button>" +
            "</div>";
        }
      });

      var counts = {};
      this.state.items.forEach(function (it) { counts[it.tabId] = (counts[it.tabId] || 0) + 1; });
      toArray(this.catalogEl.querySelectorAll("[data-cb-tabdot]")).forEach(function (dot) {
        var n = counts[dot.dataset.cbTabdot] || 0;
        if (String(n) === dot.dataset.n) return;
        dot.dataset.n = String(n);
        dot.className = n ? "cb-tabdot" : "";
        dot.textContent = n ? String(n) : "";
      });

      var roomEl = this.catalogEl.querySelector("[data-cb-room]");
      if (roomEl) roomEl.textContent = this.basket() ? this.roomText(q) : "";
    }

    /* ---------- piezas ---------- */

    addItem(product, fromEl) {
      if (!product) return;
      var q = this.cupo();
      if (!q.room[product.type]) {
        this.toast(q.full ? "Tu canasta ya está completa" : product.type === "gourmet"
          ? "Ya no cabe más gourmet en esta canasta"
          : "Ya no caben más botellas en esta canasta");
        return;
      }
      var wasFull = q.full;
      var item = {
        uid: this.uid++,
        key: product.key,
        productId: product.productId,
        handle: product.handle,
        title: product.title,
        vendor: product.vendor,
        price: product.price,
        image: product.image,
        type: product.type,
        tabId: product.tabId,
        fresh: true
      };
      this.state.items.push(item);
      this.update();

      var reduce = reduceMotion();
      if (!reduce) this.fly(fromEl, this.flyTarget(item));
      if (isPocket()) this.bumpBadge(reduce ? 0 : 560);
      if (!wasFull && this.isComplete()) this.celebrate(reduce);
    }

    removeOne(key) {
      for (var i = this.state.items.length - 1; i >= 0; i--) {
        if (this.state.items[i].key === key) {
          this.state.items.splice(i, 1);
          break;
        }
      }
      this.update();
    }

    removeUid(uid) {
      this.state.items = this.state.items.filter(function (it) { return it.uid !== uid; });
      this.update();
    }

    /* ---------- canasta lateral / hoja móvil ---------- */

    bindBasketViews() {
      var self = this;
      this.root.addEventListener("click", function (event) {
        var x = event.target.closest("[data-cb-remove]");
        if (x && self.root.contains(x)) {
          self.removeUid(parseInt(x.dataset.cbRemove, 10));
          return;
        }
        var sugg = event.target.closest("[data-cb-sugg]");
        if (sugg) {
          self.addItem(self.findProduct(sugg.dataset.cbSugg), sugg.parentNode.querySelector(".cb-sugg__media"));
        }
      });
    }

    /* Lugares agrupados por tipo: en «fijo» una fila de botellas y otra de
       gourmet; en «mezcla» un solo grupo con el cupo total. */
    slotGroups(q) {
      var b = this.basket();
      var items = this.state.items;
      if (this.config.rule === "mezcla") {
        var sorted = items.filter(function (it) { return it.type !== "gourmet"; })
          .concat(items.filter(function (it) { return it.type === "gourmet"; }));
        return [{ id: "all", label: "Lugares", size: q.capacity, items: sorted }];
      }
      return [
        { id: "botella", label: "Botellas", size: b.bottles, items: items.filter(function (it) { return it.type !== "gourmet"; }) },
        { id: "gourmet", label: "Gourmet", size: b.gourmet, items: items.filter(function (it) { return it.type === "gourmet"; }) }
      ].filter(function (g) { return g.size > 0; });
    }

    slotsHtml(group) {
      var html = "";
      var cols = Math.min(6, Math.max(3, group.size));
      for (var i = 0; i < group.size; i++) {
        var it = group.items[i];
        var cls = "cb-slot" + (it ? " is-filled" : "") + (i === group.items.length ? " is-next" : "");
        html += '<div class="' + cls + '" data-cb-slot="' + group.id + "-" + i + '"' +
          (it ? ' title="' + esc(it.title) + '"' : ' aria-label="Lugar ' + (i + 1) + ' vacío"') + ">" +
          '<span class="cb-slot__n">' + (i + 1) + "</span>";
        if (it) {
          html +=
            '<div class="cb-slot__fill"' + (it.fresh ? ' style="animation-delay:' + (reduceMotion() || isPocket() ? 0 : 540) + 'ms"' : ' style="animation:none"') + ">" +
              (it.image
                ? '<img src="' + esc(sizedImage(it.image, 120)) + '" alt="' + esc(it.title) + '" loading="lazy" width="120" height="120">'
                : '<span class="cb-slot__n">' + esc(it.title.charAt(0)) + "</span>") +
            "</div>" +
            '<button type="button" class="cb-slot__x" data-cb-remove="' + it.uid + '" aria-label="Quitar ' + esc(it.title) + '">✕</button>';
        }
        html += "</div>";
      }
      return (
        '<div class="cb-slots__group">' +
          '<div class="builder-step__label">' + esc(group.label) + " · " + group.items.length + " / " + group.size + "</div>" +
          '<div class="cb-slots" style="--cols:' + cols + '">' + html + "</div>" +
        "</div>"
      );
    }

    /* Sugerencias con lo ya cargado (sin pedir más catálogo): primero lo que
       acerca al envío gratis, luego lo que llena el tipo que falta. */
    suggestions(q, total) {
      if (!this.config.showSuggestions || q.full || !q.counts.total) return [];
      var self = this;
      var have = this.qty();
      var pool = [];
      Object.keys(this.catalogs).forEach(function (id) {
        var entry = self.catalogs[id];
        if (entry.status !== "ready") return;
        entry.products.forEach(function (p) {
          if (!have[p.key] && q.room[p.type] > 0) pool.push(p);
        });
      });
      var out = [];
      var push = function (p, why) {
        if (p && out.length < 3 && !out.some(function (o) { return o.p.key === p.key; })) out.push({ p: p, why: why });
      };
      var gap = this.config.freeShip - total;
      if (this.config.freeShip > 0 && gap > 0) {
        push(pool.filter(function (p) { return p.price >= gap && p.price <= gap + 450; })
          .sort(function (a, b) { return a.price - b.price; })[0], "Con este llegas al envío gratis");
      }
      if (q.room.botella && !q.counts.botella) push(pool.filter(function (p) { return p.type === "botella"; })[0], "Falta la botella");
      if (q.room.gourmet && !q.counts.gourmet) push(pool.filter(function (p) { return p.type === "gourmet"; })[0], "Para acompañar");
      return out;
    }

    renderBasket() {
      var b = this.basket();
      var q = this.cupo();
      var items = this.state.items;
      var itemsTotal = items.reduce(function (sum, it) { return sum + it.price; }, 0);
      var total = itemsTotal + (b ? b.price : 0);
      var self = this;

      var html = "";
      if (!b) {
        html = '<div class="cb-empty"><div><strong>Elige una canasta</strong>Vuelve al paso 1 para elegir su tamaño.</div></div>';
      } else {
        var heading = q.full ? "¡Canasta completa!" : items.length ? "Va tomando forma" : "Empieza a llenarla";
        html +=
          '<div class="cb-basket__head" data-cb-basket-head>' +
            '<div><span class="mini-label">Tu canasta · ' + esc(b.title) + "</span><h4>" + heading + "</h4></div>" +
            '<div class="cb-count" aria-live="polite" aria-label="' + items.length + " de " + q.capacity + ' productos">' +
              "<strong>" + items.length + "</strong> / " + q.capacity +
            "</div>" +
          "</div>" +
          '<div class="ship-progress ship-progress--fill" role="progressbar" aria-valuemin="0" aria-valuemax="' + q.capacity +
            '" aria-valuenow="' + items.length + '" aria-label="Avance de la canasta">' +
            '<span class="ship-progress__fill" style="width:' + (q.capacity ? (items.length / q.capacity) * 100 : 0) + '%"></span></div>';
        html += this.slotGroups(q).map(function (g) { return self.slotsHtml(g); }).join("");

        html += items.length
          ? '<p class="cb-hint">' + esc(this.roomText(q)) + " Toca ✕ en un producto para quitarlo.</p>"
          : '<div class="cb-empty"><div><strong>Tu canasta está vacía</strong>' + esc(this.roomText(q)) + "</div></div>";
        if (q.full) html += '<div class="cb-complete"><i>✓</i><span>Lista para regalar.</span></div>';

        var sugg = this.suggestions(q, total);
        if (sugg.length) {
          html +=
            '<div class="builder-step"><div class="builder-step__label">Para completar tu canasta</div><div class="cb-sugg">' +
            sugg.map(function (s) {
              return (
                '<div class="cb-sugg__row">' +
                  '<div class="cb-sugg__media">' +
                    (s.p.image ? '<img src="' + esc(sizedImage(s.p.image, 120)) + '" alt="" loading="lazy" width="40" height="44">' : "") +
                  "</div>" +
                  '<div class="cb-sugg__copy"><strong title="' + esc(s.p.title) + '">' + esc(s.p.title) + "</strong>" +
                    "<span>" + esc(s.why) + " · " + money(s.p.price) + "</span></div>" +
                  '<button type="button" class="cb-sugg__add" data-cb-sugg="' + esc(s.p.key) + '" aria-label="Agregar ' + esc(s.p.title) + '">+ Agregar</button>' +
                "</div>"
              );
            }).join("") +
            "</div></div>";
        }

        var gap = this.config.freeShip - total;
        var pct = this.config.freeShip > 0 ? Math.min(100, (total / this.config.freeShip) * 100) : 0;
        html +=
          '<div class="builder-step"><div class="cb-totals">' +
            "<div><span>" + esc(b.title) + "</span><span>" + money(b.price) + "</span></div>" +
            "<div><span>" + plural(items.length, "producto", "productos") + "</span><span>" + money(itemsTotal) + "</span></div>" +
            '<div class="is-total"><span>Total de tu canasta</span><strong class="recap-subtotal">' + money(total) + "</strong></div>" +
          "</div>";
        if (this.config.freeShip > 0) {
          html +=
            '<div class="ship-progress" style="margin-top:10px"><span class="ship-progress__fill" style="width:' + pct + '%"></span></div>' +
            '<p class="cb-ship' + (gap <= 0 ? " is-free" : "") + '">' +
              (gap > 0
                ? "Te faltan <strong>" + money(gap) + "</strong> para el envío gratis a todo México."
                : "<strong>✓ Tu canasta ya tiene envío gratis a todo México.</strong>") +
            "</p>";
        }
        html += "</div>";
      }

      this.basketViews.forEach(function (view) {
        var aside = view.classList.contains("cb-basket--aside");
        var cta = "";
        if (aside && self.state.step === 2 && view.closest('[data-cb-panel="2"]')) {
          cta = '<div class="builder-step"><button type="button" class="btn btn--primary btn--full" data-cb-go="3">' +
            esc(self.ctaLabel()) + '</button><p class="sim-note" style="text-align:center">Sigue: dedicatoria y resumen.</p></div>';
        } else if (aside && view.closest('[data-cb-panel="3"]')) {
          cta = '<div class="builder-step"><button type="button" class="btn btn--checkout btn--full" data-cb-submit></button></div>';
        }
        view.innerHTML = html + cta;
        view.classList.toggle("is-complete", q.full);
      });
      /* la animación de entrada del lugar solo corre al llegar la pieza */
      items.forEach(function (it) { it.fresh = false; });

      if (this.sheet) {
        this.sheet.classList.toggle("is-complete", q.full);
        var badge = this.sheet.querySelector("[data-cb-badge]");
        if (badge) badge.textContent = items.length + "/" + q.capacity;
        var mini = this.sheet.querySelector("[data-cb-sheet-mini]");
        if (mini) mini.style.width = (q.capacity ? (items.length / q.capacity) * 100 : 0) + "%";
        var sheetCta = this.sheet.querySelector("[data-cb-sheet-cta]");
        if (sheetCta && this.state.step >= 2) {
          sheetCta.innerHTML =
            "<div><small>Total</small><strong>" + money(total) + "</strong></div>" +
            (this.state.step === 3
              ? '<button type="button" class="btn btn--checkout" data-cb-submit></button>'
              : '<button type="button" class="btn btn--primary" data-cb-go="3">' + esc(this.ctaLabel()) + "</button>");
        }
      }
      this.renderSubmit();
    }

    /* ---------- paso 3 · dedicatoria + resumen ---------- */

    giftErrors() {
      var g = this.state.gift;
      return {
        from: g.on && g.from.trim().length < 2,
        to: g.on && g.to.trim().length < 2
      };
    }

    /* El formulario se pinta una vez (y al «Armar otra»): renderGift solo
       actualiza tarjeta, contador y errores para no perder foco ni cursor. */
    renderGiftShell() {
      if (!this.giftEl) return;
      var g = this.state.gift;
      var uid = "cb-" + this.root.dataset.sectionId;
      var field = function (key, label, placeholder, error) {
        return (
          '<div class="cb-field"><label for="' + uid + "-" + key + '">' + label + "</label>" +
            '<input id="' + uid + "-" + key + '" type="text" maxlength="' + NAME_MAX + '" autocomplete="off"' +
            ' data-cb-gift-input="' + key + '" placeholder="' + placeholder + '" aria-describedby="' + uid + "-" + key + '-err">' +
            '<p class="checkout-error" id="' + uid + "-" + key + '-err" data-cb-field-error="' + key + '" hidden>' + error + "</p>" +
          "</div>"
        );
      };
      var quick = this.config.giftQuick.map(function (m) {
        m = m.slice(0, MSG_MAX);
        return '<button type="button" class="choice" data-cb-quick="' + esc(m) + '" aria-pressed="false">' + esc(m) + "</button>";
      }).join("");

      this.giftEl.innerHTML =
        '<div style="display:grid;gap:20px;min-width:0">' +
          '<div class="recap">' +
            '<div class="recap__head"><span class="mini-label">Tarjeta de regalo</span><h4>¿Quién envía y quién recibe?</h4></div>' +
            '<label class="cb-toggle"><input type="checkbox" data-cb-gift-on>Incluir tarjeta con dedicatoria (sin costo)</label>' +
            "<div data-cb-gift-fields>" +
              '<div class="cb-field__row" style="margin-top:12px">' +
                field("from", "De parte de", "Tu nombre o el de tu empresa", "Escribe quién envía la canasta (mínimo 2 letras).") +
                field("to", "Para", "Nombre de quien la recibe", "Escribe a quién va dirigida (mínimo 2 letras).") +
              "</div>" +
              '<div class="cb-field about-me"><label for="' + uid + '-msg">Mensaje <em>(opcional)</em></label>' +
                '<textarea id="' + uid + '-msg" rows="3" maxlength="' + MSG_MAX + '" data-cb-gift-input="msg" placeholder="Unas líneas para acompañar el regalo…"></textarea>' +
                '<span class="cb-counter" data-cb-counter aria-live="polite"></span>' +
              "</div>" +
              (quick ? '<div class="choice-group" style="margin-bottom:18px" aria-label="Mensajes rápidos">' + quick + "</div>" : "") +
              '<div class="builder-step__label">Así se verá tu tarjeta</div>' +
              '<div class="cb-note"><small>Alcornoque</small>' +
                '<div class="cb-note__to" data-cb-note="to"></div>' +
                '<div class="cb-note__msg" data-cb-note="msg"></div>' +
                '<div class="cb-note__from" data-cb-note="from"></div>' +
              "</div>" +
            "</div>" +
            '<label class="cb-toggle"><input type="checkbox" data-cb-gift-hide>Es un regalo: no incluir precios en el paquete</label>' +
          "</div>" +
          '<div class="recap" data-cb-recap></div>' +
          '<p class="checkout-error" data-cb-error role="alert" hidden></p>' +
        "</div>";

      var el = this.giftEl;
      ["from", "to", "msg"].forEach(function (k) {
        el.querySelector('[data-cb-gift-input="' + k + '"]').value = g[k];
      });
      el.querySelector("[data-cb-gift-on]").checked = g.on;
      el.querySelector("[data-cb-gift-hide]").checked = g.hide;
      this.renderGift();
      this.renderRecap();
    }

    renderGift() {
      var el = this.giftEl;
      var fields = el && el.querySelector("[data-cb-gift-fields]");
      if (!fields) return;
      var g = this.state.gift;
      var err = this.giftErrors();
      var tried = this.state.tried;
      fields.hidden = !g.on;
      ["from", "to"].forEach(function (k) {
        var show = tried && err[k];
        el.querySelector('[data-cb-gift-input="' + k + '"]').setAttribute("aria-invalid", show ? "true" : "false");
        el.querySelector('[data-cb-field-error="' + k + '"]').hidden = !show;
      });
      el.querySelector("[data-cb-counter]").textContent = g.msg.length + " / " + MSG_MAX;
      el.querySelector('[data-cb-note="to"]').textContent = "Para " + (g.to.trim() || "…");
      el.querySelector('[data-cb-note="msg"]').textContent = g.msg.trim() || "Tu mensaje aparecerá aquí.";
      el.querySelector('[data-cb-note="from"]').textContent = "— " + (g.from.trim() || "…");
      toArray(el.querySelectorAll("[data-cb-quick]")).forEach(function (btn) {
        var on = btn.dataset.cbQuick === g.msg;
        btn.classList.toggle("is-active", on);
        btn.setAttribute("aria-pressed", on ? "true" : "false");
      });
    }

    /* Piezas agrupadas por variante, en orden de llegada: { item, n }. */
    grouped() {
      var map = {};
      var out = [];
      this.state.items.forEach(function (it) {
        if (!map[it.key]) out.push((map[it.key] = { item: it, n: 0 }));
        map[it.key].n++;
      });
      return out;
    }

    renderRecap() {
      var el = this.giftEl && this.giftEl.querySelector("[data-cb-recap]");
      if (!el) return;
      var b = this.basket();
      var q = this.cupo();
      var html =
        '<div class="recap__head"><span class="mini-label">Tu canasta producto por producto</span><h4>' +
        (b ? esc(b.title) + " · " + q.counts.total + " de " + q.capacity : "Elige una canasta") + "</h4></div>";
      var rows = this.grouped();
      if (rows.length) {
        html += '<div class="recap-list">' + rows.map(function (row) {
          var it = row.item;
          var key = esc(it.key);
          return (
            '<div class="recap-row">' +
              '<div class="recap-row__media">' +
                (it.image ? '<img src="' + esc(sizedImage(it.image, 128)) + '" alt="" loading="lazy" width="64" height="64">' : "") +
              "</div>" +
              '<div class="recap-row__copy"><strong>' + esc(it.title) + "</strong>" +
                "<span>" + money(it.price) + (row.n > 1 ? " × " + row.n + " = " + money(it.price * row.n) : "") + "</span></div>" +
              '<div class="recap-row__actions">' +
                '<span class="cb-row-stepper">' +
                  '<button type="button" class="mix-split__btn" data-cb-recap-dec="' + key + '" aria-label="Quitar uno de ' + esc(it.title) + '">−</button>' +
                  '<b aria-live="polite">' + row.n + "</b>" +
                  '<button type="button" class="mix-split__btn" data-cb-recap-inc="' + key + '" aria-label="Agregar otro ' + esc(it.title) + '"' +
                    (q.room[it.type] ? "" : " disabled") + ">+</button>" +
                "</span>" +
                '<button type="button" class="recap-action recap-action--danger" data-cb-recap-remove="' + key + '">Quitar</button>' +
              "</div>" +
            "</div>"
          );
        }).join("") + "</div>";
      } else {
        html += '<div class="cb-empty"><div><strong>Tu canasta está vacía</strong>Vuelve al catálogo para llenarla.</div></div>';
      }
      if (!this.canContinue()) {
        html +=
          '<p class="checkout-error" style="margin-top:12px">' + esc(this.ctaLabel()) + " para agregarla al carrito. " +
          '<button type="button" class="catalog-refresh" data-cb-go="2">Volver al catálogo</button></p>';
      }
      el.innerHTML = html;
    }

    renderSubmit() {
      var off = !this.canContinue() || this.state.adding;
      var label = this.state.adding
        ? '<span class="cb-spin" aria-hidden="true"></span>Agregando…'
        : "Agregar canasta al carrito";
      toArray(this.root.querySelectorAll("[data-cb-submit]")).forEach(function (btn) {
        btn.disabled = off;
        btn.innerHTML = label;
      });
    }

    setError(message) {
      var el = this.giftEl && this.giftEl.querySelector("[data-cb-error]");
      if (!el) return;
      el.hidden = !message;
      el.textContent = message || "";
      if (message) el.scrollIntoView({ behavior: reduceMotion() ? "auto" : "smooth", block: "nearest" });
    }

    bindGift() {
      var self = this;
      if (this.giftEl) {
        this.giftEl.addEventListener("input", function (event) {
          var key = event.target.dataset.cbGiftInput;
          if (!key) return;
          self.state.gift[key] = event.target.value;
          self.renderGift();
        });
        this.giftEl.addEventListener("change", function (event) {
          var t = event.target;
          if (t.matches("[data-cb-gift-on]")) self.state.gift.on = t.checked;
          else if (t.matches("[data-cb-gift-hide]")) self.state.gift.hide = t.checked;
          else return;
          self.renderGift();
        });
        this.giftEl.addEventListener("click", function (event) {
          var t = event.target;
          var quick = t.closest("[data-cb-quick]");
          if (quick) {
            self.state.gift.msg = quick.dataset.cbQuick;
            self.giftEl.querySelector('[data-cb-gift-input="msg"]').value = self.state.gift.msg;
            self.renderGift();
            return;
          }
          var btn = t.closest("[data-cb-recap-dec],[data-cb-recap-inc],[data-cb-recap-remove]");
          if (!btn) return;
          var d = btn.dataset;
          var key = d.cbRecapDec || d.cbRecapInc || d.cbRecapRemove;
          var attr = d.cbRecapDec ? "data-cb-recap-dec" : d.cbRecapInc ? "data-cb-recap-inc" : null;
          if (d.cbRecapRemove) {
            self.state.items = self.state.items.filter(function (it) { return it.key !== key; });
            self.update();
          } else if (d.cbRecapDec) {
            self.removeOne(key);
          } else {
            /* otra unidad de la misma pieza: conserva el tipo con el que entró */
            self.addItem(self.state.items.filter(function (it) { return it.key === key; })[0], null);
          }
          /* el resumen se re-pinta: devolver el foco al mismo botón */
          var again = attr && self.giftEl.querySelector("[" + attr + '="' + key + '"]');
          if (again && !again.disabled) again.focus();
        });
      }
      this.root.addEventListener("click", function (event) {
        if (event.target.closest("[data-cb-submit]")) self.submit();
        else if (event.target.closest("[data-cb-reset]")) self.reset();
      });
    }

    /* Un solo POST /cart/add.js: la canasta (con la dedicatoria) + cada pieza,
       todas con la misma propiedad visible «Canasta». Nunca vacía el carrito. */
    submit() {
      var self = this;
      var b = this.basket();
      if (this.state.adding || !b || !this.canContinue()) return;
      this.state.tried = true;
      this.renderGift();
      var err = this.giftErrors();
      if (err.from || err.to) {
        this.setSheet(false);
        var bad = this.giftEl.querySelector('[data-cb-gift-input="' + (err.from ? "from" : "to") + '"]');
        if (bad) bad.focus();
        return;
      }
      if (!b.variantId) {
        this.setError("Esta canasta no tiene un producto configurado. Elige otra en el paso 1.");
        return;
      }

      var g = this.state.gift;
      var code = basketCode();
      var tag = b.title + " · " + code;
      var props = { Canasta: tag };
      if (g.on) {
        props.Para = g.to.trim();
        props.De = g.from.trim();
        if (g.msg.trim()) props.Mensaje = g.msg.trim();
      }
      if (g.hide) props["Sin precios"] = "Sí";
      var items = [{ id: Number(b.variantId), quantity: 1, properties: props }].concat(
        this.grouped().map(function (row) {
          return { id: Number(row.item.key), quantity: row.n, properties: { Canasta: tag } };
        })
      );
      var count = this.state.items.length;
      var total = b.price + this.state.items.reduce(function (sum, it) { return sum + it.price; }, 0);

      var base = shopRoot();
      if (base.charAt(base.length - 1) !== "/") base += "/";
      this.setError("");
      this.state.adding = true;
      this.renderSubmit();

      fetch(base + "cart/add.js", {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        credentials: "same-origin",
        body: JSON.stringify({ items: items })
      })
        .then(function (res) {
          return res.json().catch(function () { return {}; }).then(function (data) {
            if (!res.ok) {
              var error = new Error(data && data.description ? data.description : "HTTP " + res.status);
              error.status = res.status;
              throw error;
            }
            return data;
          });
        })
        .then(function () {
          self.refreshCart();
          self.showDone(code, b, count, total);
        })
        .catch(function (error) {
          if (window.console) console.warn("Arma tu canasta: no se pudo agregar al carrito", error);
          /* Un 422 de stock puede dejar cantidades parciales (doc de la Cart
             API): el drawer debe reflejar lo que sí entró. */
          if (error && error.status === 422) {
            self.refreshCart();
            self.setError(
              "No pudimos agregar tu canasta completa: " + error.message +
              " Revisa tu carrito y ajusta las piezas antes de volver a intentarlo."
            );
          } else {
            self.setError("No pudimos agregar tu canasta al carrito. Revisa tu conexión e inténtalo de nuevo.");
          }
          self.toast("No pudimos agregar tu canasta");
        })
        .then(function () {
          self.state.adding = false;
          self.renderSubmit();
        });
    }

    refreshCart() {
      document.documentElement.dispatchEvent(new CustomEvent("cart:refresh", { bubbles: true }));
    }

    showDone(code, b, count, total) {
      var done = this.root.querySelector('[data-cb-panel="done"]');
      if (!done) return;
      var g = this.state.gift;
      this.panels.forEach(function (panel) {
        panel.classList.toggle("is-active", panel === done);
      });
      this.setSheet(false);
      if (this.sheet) this.sheet.hidden = true;

      var text = done.querySelector("[data-cb-done-text]");
      if (text) {
        if (text.dataset.base == null) text.dataset.base = text.textContent.trim();
        text.textContent = text.dataset.base +
          (g.on && g.to.trim() ? " Va con tu tarjeta para " + g.to.trim() + "." : "");
      }
      var grid = done.querySelector("[data-cb-done-grid]");
      if (grid) {
        var free = this.config.freeShip > 0 && total >= this.config.freeShip;
        grid.innerHTML =
          "<div><small>Canasta</small><strong>" + esc(b.title) + " · " + plural(count, "producto", "productos") + "</strong></div>" +
          "<div><small>Código en tu carrito</small><strong>" + esc(code) + "</strong></div>" +
          "<div><small>Total</small><strong>" + money(total) + (free ? " · envío gratis" : "") + "</strong></div>";
      }
      if (this.builder) this.builder.scrollIntoView({ behavior: reduceMotion() ? "auto" : "smooth", block: "start" });
    }

    /* «Armar otra»: canasta vacía y dedicatoria en blanco; el carrito no se toca. */
    reset() {
      this.state.items = [];
      this.state.gift = newGift();
      this.state.tried = false;
      this.renderGiftShell();
      this.update();
      this.goPanel(1);
    }

    /* ---------- animaciones (todas se saltan con prefers-reduced-motion) ---------- */

    flyTarget(item) {
      if (isPocket()) return this.sheet && this.sheet.querySelector("[data-cb-badge]");
      var aside = this.root.querySelector('[data-cb-panel="2"] .cb-basket--aside');
      if (!aside) return null;
      var group = this.config.rule === "mezcla" ? "all" : item.type;
      var index = this.slotGroups(this.cupo()).filter(function (g) { return g.id === group; })[0];
      if (!index) return null;
      var at = index.items.indexOf(item);
      return aside.querySelector('[data-cb-slot="' + group + "-" + at + '"]');
    }

    fly(fromEl, toEl) {
      var layer = this.flyLayer;
      if (!fromEl || !toEl || !layer || !fromEl.animate) return;
      var img = fromEl.querySelector("img");
      if (!img) return;
      var a = fromEl.getBoundingClientRect();
      var b = toEl.getBoundingClientRect();
      var ghost = document.createElement("div");
      ghost.className = "cb-ghost";
      ghost.appendChild(img.cloneNode());
      ghost.style.left = a.left + "px";
      ghost.style.top = a.top + "px";
      ghost.style.width = a.width + "px";
      ghost.style.height = a.height + "px";
      layer.appendChild(ghost);
      var dx = b.left + b.width / 2 - (a.left + a.width / 2);
      var dy = b.top + b.height / 2 - (a.top + a.height / 2);
      var s = Math.max(0.14, Math.min(0.5, b.width / a.width));
      ghost.animate(
        [
          { transform: "translate(0,0) scale(1) rotate(0deg)", opacity: 1 },
          { transform: "translate(" + dx * 0.45 + "px," + (Math.min(dy * 0.45, 0) - 80) + "px) scale(" + (1 + s) / 2 + ") rotate(-10deg)", opacity: 1, offset: 0.45 },
          { transform: "translate(" + dx + "px," + dy + "px) scale(" + s + ") rotate(4deg)", opacity: 0.3 }
        ],
        { duration: 600, easing: "cubic-bezier(.45,0,.25,1)" }
      ).onfinish = function () { ghost.remove(); };
    }

    bumpBadge(delay) {
      var badge = this.sheet && this.sheet.querySelector("[data-cb-badge]");
      if (!badge) return;
      setTimeout(function () {
        badge.classList.remove("is-bump");
        void badge.offsetWidth;
        badge.classList.add("is-bump");
      }, delay);
    }

    celebrate(reduce) {
      var self = this;
      setTimeout(function () {
        self.toast(self.canContinue()
          ? "¡Canasta completa! Ya puedes escribir tu dedicatoria."
          : "¡Canasta completa!");
        if (reduce || !self.flyLayer) return;
        var el = isPocket()
          ? self.sheet && self.sheet.querySelector("[data-cb-badge]")
          : self.root.querySelector('[data-cb-panel="2"] .cb-basket--aside [data-cb-basket-head]');
        if (!el) return;
        var r = el.getBoundingClientRect();
        var burst = document.createElement("div");
        burst.className = "cb-confetti";
        burst.style.left = r.left + r.width / 2 + "px";
        burst.style.top = r.top + r.height / 2 + "px";
        for (var i = 0; i < 26; i++) {
          var bit = document.createElement("i");
          var angle = Math.random() * Math.PI * 2;
          var dist = 50 + Math.random() * 90;
          var w = 6 + Math.random() * 6;
          bit.style.setProperty("--tx", Math.cos(angle) * dist + "px");
          bit.style.setProperty("--ty", Math.sin(angle) * dist - 40 + "px");
          bit.style.setProperty("--r", Math.round(Math.random() * 720 - 360) + "deg");
          bit.style.background = CONFETTI[i % CONFETTI.length];
          bit.style.width = w + "px";
          bit.style.height = w * 0.55 + "px";
          bit.style.animationDelay = Math.round(Math.random() * 120) + "ms";
          burst.appendChild(bit);
        }
        self.flyLayer.appendChild(burst);
        setTimeout(function () { burst.remove(); }, 1700);
      }, reduce ? 0 : isPocket() ? 600 : 900);
    }

    /* ---------- render global ---------- */

    update() {
      this.renderBaskets();
      this.renderBasket();
      this.updateCards();
      this.renderRecap();
      this.renderNav();
    }

    /* ---------- navegación multistep ---------- */

    canReach(step) {
      if (step <= 1) return true;
      if (step === 2) return !!this.basket();
      return this.canContinue();
    }

    goPanel(n) {
      var step = clampStep(n);
      while (step > 1 && !this.canReach(step)) step--;
      this.state.step = step;

      this.panels.forEach(function (panel) {
        panel.classList.toggle("is-active", parseInt(panel.dataset.cbPanel, 10) === step);
      });
      if (this.sheet) {
        this.sheet.hidden = step === 1;
        if (step === 1) this.setSheet(false);
      }
      if (step === 2 && this.gridEl && !this.gridEl.childNodes.length) this.renderCatalog();
      this.renderBasket();
      this.renderRecap();
      this.renderNav();

      if (!this.booting && this.builder) {
        this.builder.scrollIntoView({ behavior: reduceMotion() ? "auto" : "smooth", block: "start" });
      }
    }

    renderNav() {
      var self = this;
      var step = this.state.step;
      this.steps.forEach(function (item) {
        var value = parseInt(item.dataset.cbStep, 10);
        item.classList.toggle("is-active", value === step);
        item.classList.toggle("is-done", value < step);
        item.disabled = !self.canReach(value);
        if (value === step) item.setAttribute("aria-current", "step");
        else item.removeAttribute("aria-current");
        var num = item.querySelector("span");
        if (num) num.textContent = value < step ? "✓" : String(value);
      });
      this.lines.forEach(function (line, i) {
        line.classList.toggle("is-done", i + 1 < step);
      });
      var label = this.ctaLabel();
      toArray(this.root.querySelectorAll("[data-cb-go]")).forEach(function (btn) {
        var target = parseInt(btn.dataset.cbGo, 10);
        btn.disabled = !self.canReach(target);
        if (target === 3) btn.textContent = label;
      });
    }

    bindNav() {
      var self = this;
      this.steps.forEach(function (item) {
        item.addEventListener("click", function () {
          self.goPanel(parseInt(item.dataset.cbStep, 10));
        });
      });
      this.root.addEventListener("click", function (event) {
        var go = event.target.closest("[data-cb-go]");
        if (go && self.root.contains(go)) {
          self.setSheet(false);
          self.goPanel(parseInt(go.dataset.cbGo, 10));
          return;
        }
        if (event.target.closest("[data-cb-sheet-toggle]")) {
          self.setSheet(!self.sheet.classList.contains("is-open"));
        } else if (event.target.closest("[data-cb-sheet-close]")) {
          self.setSheet(false);
        }
      });
    }

    setSheet(open) {
      if (!this.sheet) return;
      this.sheet.classList.toggle("is-open", open);
      var bar = this.sheet.querySelector("[data-cb-sheet-toggle]");
      if (bar) bar.setAttribute("aria-expanded", open ? "true" : "false");
    }

    toast(message) {
      var el = this.root.querySelector("[data-cb-toast]");
      if (!el) return;
      el.textContent = message;
      el.classList.add("show");
      clearTimeout(this._toastTimer);
      this._toastTimer = setTimeout(function () {
        el.classList.remove("show");
      }, 2600);
    }
  }

  function boot() {
    toArray(document.querySelectorAll("[data-canasta-builder]")).forEach(function (el) {
      if (el.__canastaBuilder) return;
      el.__canastaBuilder = new CanastaBuilder(el);
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }
  /* Editor de temas: la sección se re-renderiza con markup nuevo. */
  document.addEventListener("shopify:section:load", boot);
})();
